import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { workplaceApi } from '../../services/api';
import { Workplace, DangerClass } from '../../types';
import WorkplaceDetailModal from '../../components/Workplace/WorkplaceDetailModal';
import { getProgressTheme } from '../../utils/progressTheme';
import Swal from 'sweetalert2';
import './Dashboard.css';

// 18 Standart Resmi İSG Belge Tanımları
const STANDARD_DOC_KEYS = [
  'riskAnalysis', 'emergencyPlan', 'trainingPlan', 'workPlan',
  'instructions', 'ek2', 'trainings', 'employeeRepresentative',
  'annualEvaluation', 'kkdDelivery', 'firstAid', 'machinePeriodicCheck',
  'electricalInstallationCheck', 'detectionSuggestionBook', 'emergencyDrill',
  'fieldAuditReport', 'test', 'jobTraining'
];

export const checkIsCompletedThisMonth = (wp: Workplace): boolean => {
  const now = new Date();
  const currentMonthNum = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  // 1. monthlyVisits nesnesi kontrolü (örnek: { 9: true } veya { "9": true })
  if (wp.monthlyVisits) {
    if (wp.monthlyVisits[currentMonthNum] || (wp.monthlyVisits as any)[String(currentMonthNum)]) {
      return true;
    }
  }

  // 2. visits dizisi kontrolü (bu aya ait tamamlanmış ziyaret var mı?)
  if (Array.isArray(wp.visits)) {
    const hasVisit = wp.visits.some(v => {
      if (v.status !== 'completed' || !v.visitDate) return false;
      const d = new Date(v.visitDate);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && (d.getMonth() + 1) === currentMonthNum;
    });
    if (hasVisit) return true;
  }

  return false;
};

const ALLOWED_CANCELLABLE_KEYS = [
  'employeeRepresentative',
  'machinePeriodicCheck',
  'kkdDelivery',
  'fieldAuditReport',
  'jobTraining'
];

export const calculateWorkplaceProgress = (wp: Workplace): number => {
  const cancelledRaw = Array.isArray(wp.cancelledDocuments) ? wp.cancelledDocuments : [];
  const cancelled = cancelledRaw.filter(k => ALLOWED_CANCELLABLE_KEYS.includes(k));
  const activeDocs = STANDARD_DOC_KEYS.filter(k => !cancelled.includes(k));
  const uploadedSet = new Set((wp.documents || []).map(d => d.documentType));
  const uploadedCount = activeDocs.filter(k => uploadedSet.has(k)).length;

  const docScore = activeDocs.length > 0
    ? Math.round((uploadedCount / activeDocs.length) * 50)
    : 50;

  // Ziyaret Skoru: Yalnızca içinde bulunulan cari ayda ziyaret yapılmış/tamamlanmışsa +%50 verilir.
  // Ay değiştiğinde (örneğin 1 Ekim 00:00) o ayın ziyareti henüz yapılmamışsa puan otomatik olarak 0 olur.
  const isThisMonthDone = checkIsCompletedThisMonth(wp);
  const visitScore = isThisMonthDone ? 50 : 0;

  return Math.min(100, docScore + visitScore);
};

const initialFormData = {
  id: undefined as number | undefined,
  title: '',
  dangerClass: 'low' as DangerClass,
  employeeCount: '' as unknown as number,
  sgkNo: '',
  city: '',
  district: '',
  address: '',
  contactPerson: '',
  contactPhone: '',
  notes: ''
};

const Dashboard: React.FC = () => {
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDangerFilter, setSelectedDangerFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    const saved = localStorage.getItem('dashboard_view_mode');
    return (saved === 'list' || saved === 'card') ? saved : 'card';
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Kayıtlı tüm işyerlerinin genel ziyaret ve evrak tamamlama ortalaması
  const overallProgress = useMemo(() => {
    if (!workplaces || workplaces.length === 0) return 0;
    const total = workplaces.reduce((sum, wp) => sum + calculateWorkplaceProgress(wp), 0);
    return Math.round(total / workplaces.length);
  }, [workplaces]);

  const overallTheme = useMemo(() => getProgressTheme(overallProgress), [overallProgress]);

  // Görünüm modu değiştiğinde localStorage'a kalıcı olarak kaydet
  const handleViewModeChange = (mode: 'card' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('dashboard_view_mode', mode);
  };

  // Form State (Sol Kart)
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Detay Modalı State (Sağ Kart Tıklaması)
  const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const fetchWorkplaces = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await workplaceApi.getAll({
        search: searchTerm,
        dangerClass: selectedDangerFilter
      });
      if (res.success && res.data) {
        setWorkplaces(res.data);
        setSelectedWorkplace(prev => {
          if (!prev) return null;
          const found = res.data.find((w: Workplace) => w.id === prev.id);
          return found || prev;
        });
      }
    } catch (err) {
      console.error('İşyerleri getirme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedDangerFilter]);

  // Anlık İyimser (Optimistic) Güncelleme: Modal içindeki değişiklikleri 0ms'de ana sayfaya yansıt
  const handleWorkplaceChange = useCallback((id: number, partialData: Partial<Workplace>) => {
    setWorkplaces(prev => prev.map(wp => {
      if (wp.id === id) {
        return {
          ...wp,
          ...partialData
        };
      }
      return wp;
    }));
    setSelectedWorkplace(prev => {
      if (!prev || prev.id !== id) return prev;
      return {
        ...prev,
        ...partialData
      };
    });
  }, []);

  useEffect(() => {
    fetchWorkplaces();
    setCurrentPage(1); // Filtre veya arama değiştiğinde ilk sayfaya dön
  }, [fetchWorkplaces]);

  // Telefon Numarası Formatlama: 5XX XXX XX XX (En fazla 10 rakam, 0 ile başlamaz, 5 ile başlar)
  const formatPhoneNumber = (val: string): string => {
    let digits = val.replace(/\D/g, '');
    // 0 ile başlarsa baştaki 0'ı at
    while (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    // İlk rakam girilmişse ve 5 değilse, 5 olarak düzelt
    if (digits.length > 0 && !digits.startsWith('5')) {
      digits = '5' + digits;
    }
    // En fazla 10 rakam
    digits = digits.slice(0, 10);

    let formatted = '';
    if (digits.length > 0) {
      formatted += digits.substring(0, 3);
    }
    if (digits.length > 3) {
      formatted += ' ' + digits.substring(3, 6);
    }
    if (digits.length > 6) {
      formatted += ' ' + digits.substring(6, 8);
    }
    if (digits.length > 8) {
      formatted += ' ' + digits.substring(8, 10);
    }
    return formatted;
  };

  // SGK No / DETSİS Formatlayıcı: Boşluksuz doğrudan gösterim
  const formatSskDisplay = (no: string | undefined) => {
    if (!no) return '-';
    const clean = no.trim();
    const digits = clean.replace(/\D/g, '');
    if (digits.length === 8) {
      return `DETSİS: ${digits}`;
    }
    return clean;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'contactPhone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, contactPhone: formatted }));
      return;
    }

    if (name === 'sgkNo') {
      // SGK Sicil No / DETSİS yalnızca rakam kabul eder (maksimum 26 hane)
      const digitsOnly = value.replace(/\D/g, '').slice(0, 26);
      setFormData(prev => ({ ...prev, sgkNo: digitsOnly }));
      return;
    }

    if (name === 'employeeCount') {
      const val = value === '' ? ('' as unknown as number) : Math.max(1, parseInt(value, 10) || 1);
      setFormData(prev => ({ ...prev, employeeCount: val }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      Swal.fire('Uyarı', 'Lütfen işyeri unvanını giriniz.', 'warning');
      return;
    }

    if (!formData.employeeCount || Number(formData.employeeCount) < 1) {
      Swal.fire('Uyarı', 'Lütfen geçerli bir çalışan sayısı giriniz.', 'warning');
      return;
    }

    // SGK / DETSİS Doğrulaması ve Tekillik Kontrolü
    if (formData.sgkNo && formData.sgkNo.trim()) {
      const cleanSgk = formData.sgkNo.trim();
      if (!/^\d+$/.test(cleanSgk)) {
        Swal.fire('Uyarı', 'SGK Sicil No sadece rakamlardan oluşmalıdır.', 'warning');
        return;
      }
      // DETSİS no ise tam 8 hane rakam olmalı; SGK ise max 26 karakter
      if (cleanSgk.length > 26) {
        Swal.fire('Uyarı', 'SGK sicil no 26 karakterden fazla olamaz.', 'warning');
        return;
      }
      // Kullanıcı 8 haneli girmişse DETSİS kabul edilir. Eğer 8 haneden kısa ise uyaralım
      if (cleanSgk.length < 8) {
        Swal.fire('Uyarı', 'SGK Sicil No en az 8 haneli (DETSİS) veya en fazla 26 haneli olmalıdır.', 'warning');
        return;
      }

      // Aynı SGK sicil no ile kayıtlı işyeri kontrolü
      const duplicateWorkplace = workplaces.find(
        wp => wp.sgkNo && wp.sgkNo.trim().toLowerCase() === cleanSgk.toLowerCase() && wp.id !== formData.id
      );

      if (duplicateWorkplace) {
        Swal.fire({
          icon: 'warning',
          title: 'İşyeri Zaten Kayıtlı',
          text: `Bu SGK Sicil No ile "${duplicateWorkplace.title}" unvanlı işyeri zaten sistemde kayıtlıdır!`,
          confirmButtonColor: '#1e3c72',
          confirmButtonText: 'Tamam'
        });
        return;
      }
    }

    // Telefon Doğrulaması (Eğer girildiyse)
    if (formData.contactPhone && formData.contactPhone.trim()) {
      const phoneDigits = formData.contactPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10 || !phoneDigits.startsWith('5')) {
        Swal.fire('Uyarı', 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır (Örn: 5XX XXX XX XX).', 'warning');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (isEditing && formData.id) {
        const res = await workplaceApi.update(formData.id, formData);
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Güncellendi',
            text: 'İşyeri bilgileri başarıyla güncellendi.',
            timer: 1800,
            showConfirmButton: false
          });
          resetForm();
          fetchWorkplaces();
        }
      } else {
        const res = await workplaceApi.create(formData);
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Başarılı',
            text: 'Yeni işyeri başarıyla kaydedildi.',
            timer: 1800,
            showConfirmButton: false
          });
          resetForm();
          fetchWorkplaces();
        }
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.response?.data?.message || 'İşyeri kaydedilirken bir hata oluştu.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setIsEditing(false);
  };

  const startEdit = (wp: Workplace) => {
    setFormData({
      id: wp.id,
      title: wp.title,
      dangerClass: wp.dangerClass,
      employeeCount: wp.employeeCount,
      sgkNo: wp.sgkNo || '',
      city: wp.city || '',
      district: wp.district || '',
      address: wp.address || '',
      contactPerson: wp.contactPerson || '',
      contactPhone: wp.contactPhone || '',
      notes: wp.notes || ''
    });
    setIsEditing(true);
    // Sayfanın üstüne kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: number) => {
    const confirm = await Swal.fire({
      title: 'Emin misiniz?',
      text: 'Bu işyeri silinecektir. Devam etmek istiyor musunuz?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'İptal'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await workplaceApi.delete(id);
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Silindi',
            text: 'İşyeri başarıyla silindi.',
            timer: 1500,
            showConfirmButton: false
          });
          fetchWorkplaces();
          if (formData.id === id) resetForm();
        }
      } catch (err: any) {
        Swal.fire('Hata', err.response?.data?.message || 'Silme işlemi başarısız.', 'error');
      }
    }
  };

  const openDetail = (wp: Workplace) => {
    setSelectedWorkplace(wp);
    setIsDetailOpen(true);
  };

  const getDangerBadge = (danger: string) => {
    switch (danger) {
      case 'veryDangerous':
        return { label: 'Çok Tehlikeli', color: '#991b1b', bg: '#fee2e2', border: '#ef4444' };
      case 'dangerous':
        return { label: 'Tehlikeli', color: '#854d0e', bg: '#fef9c3', border: '#eab308' };
      default:
        return { label: 'Az Tehlikeli', color: '#0f766e', bg: '#ccfbf1', border: '#14b8a6' };
    }
  };

  // Sayfalama (Pagination): Hem Kart hem Liste görünümünde sayfa başına 6 adet işyeri
  const itemsPerPage = 6;
  const totalPages = Math.ceil(workplaces.length / itemsPerPage) || 1;
  const validCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const paginatedWorkplaces = workplaces.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="dashboard-page-container">
      {/* İki Kolonlu Ana Çalışma Alanı (Sol: Ekle Kartı, Sağ: Liste Kartı) */}
      <div className="dashboard-content-layout">
        
        {/* SOL KART: YENİ İŞYERİ EKLEME / DÜZENLEME */}
        <div className="dashboard-card workplace-form-card">
          <div className="card-header">
            <div className="header-icon-box">🏢</div>
            <div>
              <h3 className="card-title">
                {isEditing ? 'İşyeri Bilgilerini Düzenle' : 'Yeni İşyeri Ekle'}
              </h3>
              <p className="card-subtitle">
                {isEditing ? 'Bilgileri güncelleyip kaydedebilirsiniz' : 'Manuel işyeri kaydı oluşturun ve süreçlerini yönetin'}
              </p>
            </div>
          </div>

          <form onSubmit={handleFormSubmit} className="workplace-form">
            <div className="form-group full-width">
              <label>İşyeri Unvanı / Firma Adı *</label>
              <input
                type="text"
                name="title"
                placeholder="Örn: ABC Lojistik ve Taşımacılık A.Ş."
                value={formData.title}
                onChange={handleInputChange}
                required
                className="custom-input"
              />
            </div>

            <div className="form-row-danger-emp">
              <div className="form-group">
                <label>Tehlike Sınıfı *</label>
                <select
                  name="dangerClass"
                  value={formData.dangerClass}
                  onChange={handleInputChange}
                  className="custom-select"
                >
                  <option value="low">Az Tehlikeli (Yeşil)</option>
                  <option value="dangerous">Tehlikeli (Sarı)</option>
                  <option value="veryDangerous">Çok Tehlikeli (Kırmızı)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Çalışan Sayısı *</label>
                <input
                  type="number"
                  name="employeeCount"
                  min="1"
                  value={formData.employeeCount}
                  onChange={handleInputChange}
                  required
                  className="custom-input"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>SGK Sicil No / DETSİS No (Maks. 26 Hane)</label>
              <input
                type="text"
                name="sgkNo"
                value={formData.sgkNo}
                onChange={handleInputChange}
                maxLength={26}
                inputMode="numeric"
                pattern="[0-9]*"
                className="custom-input"
              />
            </div>

            <div className="form-row-city-dist">
              <div className="form-group">
                <label>İl</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="custom-input"
                />
              </div>

              <div className="form-group">
                <label>İlçe</label>
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className="custom-input"
                />
              </div>
            </div>

            <div className="form-row-person-phone">
              <div className="form-group">
                <label>Yetkili Kişi</label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  className="custom-input"
                />
              </div>

              <div className="form-group">
                <label>İletişim</label>
                <input
                  type="tel"
                  name="contactPhone"
                  placeholder="05XX XXX XX XX"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  maxLength={13}
                  className="custom-input"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Adres Bilgisi</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="custom-input"
              />
            </div>

            <div className="form-group full-width">
              <label>Özel İSG Notları</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className="custom-textarea"
              />
            </div>

            <div className="form-actions-row">
              {isEditing && (
                <button
                  type="button"
                  className="btn-cancel-edit"
                  onClick={resetForm}
                >
                  İptal
                </button>
              )}
              <button
                type="submit"
                className="btn-save-workplace"
                disabled={submitting}
              >
                {submitting
                  ? 'Kaydediliyor...'
                  : isEditing
                  ? 'Değişiklikleri Kaydet 💾'
                  : 'İşyerini Kaydet 💾'}
              </button>
            </div>
          </form>
        </div>

        {/* SAĞ KART: EKLENMİŞ İŞYERLERİ LİSTESİ */}
        <div className="dashboard-card workplace-list-card">
          <div className="list-card-header">
            <div className="list-card-title-group">
              <div className="list-title-row">
                <h3 className="card-title">Kayıtlı İşyerlerim ({workplaces.length})</h3>

                {/* Genel İlerleme Yüzdesi Bar ve Rakamı */}
                <div
                  className="dashboard-overall-progress"
                  title={`Kayıtlı ${workplaces.length} işyerinizin genel ziyaret ve evrak tamamlama ortalaması: %${overallProgress}`}
                >
                  <span className="overall-progress-label">Genel İlerleme:</span>
                  <div className="overall-progress-track">
                    <div
                      className="overall-progress-bar"
                      style={{
                        width: `${overallProgress}%`,
                        background: overallTheme.barGradient
                      }}
                    />
                  </div>
                  <span
                    className="overall-progress-badge"
                    style={{
                      color: overallTheme.textColor
                    }}
                  >
                    %{overallProgress}
                  </span>
                </div>
              </div>
              <p className="card-subtitle">Manuel olarak eklediğiniz tüm işyerlerinin listesi</p>
            </div>
            {/* Görünüm Değiştirme Butonları (Kart / Liste) */}
            <div className="view-mode-toggle-group">
              <button
                type="button"
                className={`view-mode-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('card')}
                title="Kart Görünümü"
              >
                🎴 Kart
              </button>
              <button
                type="button"
                className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                title="Liste / Tablo Görünümü"
              >
                📋 Liste
              </button>
            </div>
          </div>

          {/* Filtreleme ve Arama Çubuğu */}
          <div className="filter-controls-row">
            <div className="search-box-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="İşyeri adı veya SGK sicil no ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="danger-filter-pills">
              <button
                className={`filter-pill ${selectedDangerFilter === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedDangerFilter('all')}
              >
                Tümü
              </button>
              <button
                className={`filter-pill low ${selectedDangerFilter === 'low' ? 'active' : ''}`}
                onClick={() => setSelectedDangerFilter('low')}
              >
                Az Tehlikeli
              </button>
              <button
                className={`filter-pill med ${selectedDangerFilter === 'dangerous' ? 'active' : ''}`}
                onClick={() => setSelectedDangerFilter('dangerous')}
              >
                Tehlikeli
              </button>
              <button
                className={`filter-pill high ${selectedDangerFilter === 'veryDangerous' ? 'active' : ''}`}
                onClick={() => setSelectedDangerFilter('veryDangerous')}
              >
                Çok Tehlikeli
              </button>
            </div>
          </div>

          {/* Liste / Kart Alanı */}
          <div className="workplaces-scroll-area">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>İşyerleri yükleniyor...</p>
              </div>
            ) : workplaces.length === 0 ? (
              <div className="empty-workplaces-box">
                <span className="empty-emoji">🏢</span>
                <h4>Henüz İşyeri Eklenmedi</h4>
                <p>Sol taraftaki kartı kullanarak ilk işyerinizi manuel olarak hemen ekleyebilirsiniz.</p>
              </div>
            ) : viewMode === 'card' ? (
              /* KART GÖRÜNÜMÜ */
              <div className="workplaces-cards-grid">
                {paginatedWorkplaces.map((wp) => {
                  const badge = getDangerBadge(wp.dangerClass);
                  const wpProgress = calculateWorkplaceProgress(wp);
                  const wpTheme = getProgressTheme(wpProgress);
                  return (
                    <div
                      key={wp.id}
                      className="workplace-list-item-card"
                      onClick={() => openDetail(wp)}
                    >
                      <div className="item-card-top">
                        <span
                          className="item-danger-pill"
                          style={{
                            color: badge.color,
                            backgroundColor: badge.bg,
                            borderColor: badge.border
                          }}
                        >
                          {badge.label}
                        </span>
                        <span className="item-employee-count">
                          👥 {wp.employeeCount} Çalışan
                        </span>
                      </div>

                      <h4 className="item-title">{wp.title}</h4>

                      {/* İşyeri İlerleme Çubuğu */}
                      <div
                        className="item-card-progress-bar-wrap"
                        title={`Bu işyerinin genel ilerlemesi: %${wpProgress}`}
                      >
                        <span className="item-card-progress-label">İlerleme:</span>
                        <div className="item-card-progress-track">
                          <div
                            className="item-card-progress-fill"
                            style={{
                              width: `${wpProgress}%`,
                              background: wpTheme.barGradient
                            }}
                          />
                        </div>
                        <span
                          className="item-card-progress-text"
                          style={{
                            color: wpTheme.textColor
                          }}
                        >
                          %{wpProgress}
                        </span>
                      </div>

                      {wp.sgkNo && (
                        <div className="item-sgk-line">
                          <span className="sgk-tag">SGK / DETSİS:</span> {formatSskDisplay(wp.sgkNo)}
                        </div>
                      )}

                      <div className="item-location-line">
                        <span>📍 {wp.city ? `${wp.city} / ${wp.district || ''}` : 'Konum Belirtilmemiş'}</span>
                        {wp.contactPerson && <span>👤 {wp.contactPerson}</span>}
                      </div>

                      <div className="item-card-actions">
                        <button
                          className="item-action-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(wp.id);
                          }}
                          title="İşyerini Sil"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* LİSTE / TABLO GÖRÜNÜMÜ */
              <div className="workplaces-table-wrapper">
                <table className="workplaces-compact-table">
                  <thead>
                    <tr>
                      <th className="table-header-title">İşyeri Unvanı</th>
                      <th style={{ width: '90px' }}>Tehlike</th>
                      <th style={{ width: '90px' }}>İlerleme</th>
                      <th style={{ width: '50px', textAlign: 'center' }}>Çalışan</th>
                      <th style={{ width: '115px' }}>SGK / DETSİS</th>
                      <th style={{ width: '95px', textAlign: 'center' }}>Bu Ay Ziyaret</th>
                      <th style={{ width: '38px', textAlign: 'right' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedWorkplaces.map((wp) => {
                      const badge = getDangerBadge(wp.dangerClass);
                      const wpProgress = calculateWorkplaceProgress(wp);
                      const wpTheme = getProgressTheme(wpProgress);
                      const isThisMonthDone = checkIsCompletedThisMonth(wp);
                      return (
                        <tr
                          key={wp.id}
                          className="workplaces-table-row"
                          onClick={() => openDetail(wp)}
                        >
                          <td className="table-col-title" title={wp.title}>
                            <strong>{wp.title}</strong>
                          </td>
                          <td className="table-col-danger">
                            <span
                              className="item-danger-pill"
                              style={{
                                color: badge.color,
                                backgroundColor: badge.bg,
                                borderColor: badge.border
                              }}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="table-col-progress">
                            <div
                              className="table-progress-wrap"
                              title={`İlerleme: %${wpProgress}`}
                            >
                              <div className="table-progress-track">
                                <div
                                  className="table-progress-fill"
                                  style={{
                                    width: `${wpProgress}%`,
                                    background: wpTheme.barGradient
                                  }}
                                />
                              </div>
                              <span
                                className="table-progress-text"
                                style={{
                                  color: wpTheme.textColor
                                }}
                              >
                                %{wpProgress}
                              </span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="table-emp-badge">👥 {wp.employeeCount}</span>
                          </td>
                          <td className="table-col-sgk">
                            {formatSskDisplay(wp.sgkNo)}
                          </td>
                          <td className="table-col-visit" style={{ textAlign: 'center' }}>
                            {isThisMonthDone ? (
                              <span className="table-visit-badge completed" title="Bu ayın saha ziyareti yapıldı">
                                ✅ Yapıldı
                              </span>
                            ) : (
                              <span className="table-visit-badge pending" title="Bu ay henüz saha ziyareti yapılmadı">
                                ⏳ Bekliyor
                              </span>
                            )}
                          </td>
                          <td className="text-right" style={{ width: '38px' }} onClick={(e) => e.stopPropagation()}>
                            <div className="table-actions-cell">
                              <button
                                className="item-action-btn delete"
                                onClick={() => handleDelete(wp.id)}
                                title="İşyerini Sil"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sayfalama (Pagination) Alt Çubuğu */}
          {workplaces.length > 0 && (
            <div className="workplaces-pagination-bar">
              <div className="pagination-info">
                Toplam <strong>{workplaces.length}</strong> işyerinden <strong>{startIndex + 1} - {Math.min(startIndex + itemsPerPage, workplaces.length)}</strong> arası gösteriliyor
              </div>
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={handlePrevPage}
                    disabled={validCurrentPage <= 1}
                    title="Önceki Sayfa"
                  >
                    ◀ Önceki
                  </button>
                  <span className="pagination-page-indicator">
                    Sayfa <strong>{validCurrentPage}</strong> / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={handleNextPage}
                    disabled={validCurrentPage >= totalPages}
                    title="Sonraki Sayfa"
                  >
                    Sonraki ▶
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detay Paneli Modalı */}
      <WorkplaceDetailModal
        workplace={selectedWorkplace}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          fetchWorkplaces(true);
        }}
        onUpdate={() => fetchWorkplaces(true)}
        onWorkplaceChange={handleWorkplaceChange}
        onEdit={(wp) => startEdit(wp)}
        onDelete={(id) => handleDelete(id)}
      />
    </div>
  );
};

export default Dashboard;
